import { useMutation } from '@apollo/client';
import {
  type Integration,
  isMixpanelIntegration,
  type MixpanelIntegration,
} from '@cosuno/analytics-frontend';
import * as Sentry from '@sentry/browser';
import { useCallback, useMemo, useRef } from 'react';

import { mutationCreateAnalyticsEvent } from './api';

const settings = {
  useRawEventFormat: true,
  ignoreTrackingEnabledStatus: true,
} satisfies Integration['settings'];

const useCosunoIntegration = (): Integration => {
  const getDeviceIdRef = useRef<MixpanelIntegration['getDeviceId']>();
  const [mutate] = useMutation(mutationCreateAnalyticsEvent);

  const createAnalyticsEvent = useCallback(
    async (name: string, props: object, deviceId?: string) => {
      try {
        await mutate({ variables: { input: { name, props, deviceId } } });
      } catch (error) {
        Sentry.captureException(error);
      }
    },
    [mutate],
  );

  const trackPageView = useCallback<NonNullable<Integration['trackPageView']>>(
    (eventProps) => {
      void createAnalyticsEvent('pageView', eventProps, getDeviceIdRef.current?.());
    },
    [createAnalyticsEvent],
  );

  const trackEvent = useCallback<NonNullable<Integration['trackEvent']>>(
    (eventName, eventProps) => {
      void createAnalyticsEvent(eventName, eventProps, getDeviceIdRef.current?.());
    },
    [createAnalyticsEvent],
  );

  const onInitialization = useCallback<NonNullable<Integration['onInitialization']>>(
    (allIntegrations) => {
      const mixpanelIntegration = allIntegrations.find(isMixpanelIntegration);
      if (mixpanelIntegration) {
        getDeviceIdRef.current = mixpanelIntegration.getDeviceId;
      }
    },
    [],
  );

  return useMemo(
    () => ({
      name: 'cosuno',
      enabled: true,
      trackPageView,
      trackEvent,
      onInitialization,
      initialize: () => {},
      settings,
    }),
    [onInitialization, trackEvent, trackPageView],
  );
};

export default useCosunoIntegration;
